import { Alert, Drawer, Tag } from 'antd';
import { RowListSkeleton } from '../../../../../shared/components/RowListSkeleton';
import { formatMoney } from '../../../../../shared/policies/format-money';
import { formatLineKey } from '../../../policies/rate-line-key';
import type { RateLineView } from '../../../rate-cards.api';
import { useLineHistory, type LineVersion } from '../hooks/use-line-history';

function VersionRow({ version, isShown }: { version: LineVersion; isShown: boolean }) {
  const { line, approval } = version;

  return (
    <li className="rounded-lg border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium">{formatMoney(line.amount)}</span>
        {isShown && <Tag>This line</Tag>}
      </div>
      <p className="text-sm text-muted">
        {line.validFrom} to {line.validTo ?? 'open-ended'}
      </p>
      {approval?.decidedAt && (
        <p className="mt-1 text-xs text-muted">
          Approved {new Date(approval.decidedAt).toLocaleDateString()}
          {approval.lastDecisionReason ? `: ${approval.lastDecisionReason}` : ''}
        </p>
      )}
    </li>
  );
}

/** Every price this key has had, oldest to newest. A drawer, not a route: a read-only drill-down from the lines already on screen. */
export function LineHistoryDrawer({ line, onClose }: { line: RateLineView | null; onClose: () => void }) {
  const state = useLineHistory(line);

  return (
    <Drawer open={line !== null} onClose={onClose} title="Price history" placement="right" size={440} destroyOnClose>
      {line && <p className="mb-4 text-sm text-muted">{formatLineKey(line)}</p>}
      {state.status === 'loading' && <RowListSkeleton label="Loading price history" />}
      {state.status === 'error' && <Alert type="error" showIcon title="Couldn't load the price history. Try again." />}
      {state.status === 'loaded' && (
        <ol className="flex flex-col gap-3">
          {state.versions.map((version) => (
            <VersionRow key={version.line.id} version={version} isShown={version.line.id === line?.id} />
          ))}
        </ol>
      )}
    </Drawer>
  );
}
